import {createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useRef, useState} from "react";
import {ensureValidSession, authStorage} from "../services/authService";
import {fetchUserProfile, UserProfile} from "../services/userService";
import {isAxiosError} from "axios";

const POLL_INTERVAL_MS = 10_000;

type UserStatsContextValue = {
    profile: UserProfile | null;
    loading: boolean;
    error: string | null;
    lastUpdated: number | null;
    refresh: () => Promise<void>;
};

const UserStatsContext = createContext<UserStatsContextValue | undefined>(undefined);

export function UserStatsProvider({children}: {children: ReactNode}) {
    const [profile, setProfile] = useState<UserProfile | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [lastUpdated, setLastUpdated] = useState<number | null>(null);
    const mountedRef = useRef(false);
    const requestRef = useRef<Promise<void> | null>(null);

    useEffect(() => {
        mountedRef.current = true;
        return () => {
            mountedRef.current = false;
        };
    }, []);

    const pullProfile = useCallback(async (withLoader = false) => {
        if (requestRef.current) {
            return requestRef.current;
        }

        const request = (async () => {
            if (withLoader && mountedRef.current) {
                setLoading(true);
            }
            try {
                await ensureValidSession();
                const profileData = await fetchUserProfile();
                if (!mountedRef.current) {
                    return;
                }
                setProfile(profileData);
                setLastUpdated(Date.now());
                setError(null);
            } catch (err: any) {
                if (!mountedRef.current) {
                    return;
                }
                console.error("Failed to fetch user profile", err);
                
                // Если ошибка авторизации и это не было обработано interceptor'ом, 
                // значит токены действительно невалидны
                if (
                    (isAxiosError(err) && err.response?.status === 401) ||
                    err?.message === "Session expired" ||
                    err?.message === "Missing tokens"
                ) {
                    // Очищаем токены только если мы не на странице логина
                    // Response interceptor должен был обработать это, но на всякий случай проверяем
                    if (window.location.pathname !== '/login') {
                        authStorage.clear();
                        window.location.href = '/login';
                    }
                    return;
                }
                
                setError("Не удалось обновить статистику");
            } finally {
                if (withLoader && mountedRef.current) {
                    setLoading(false);
                }
                requestRef.current = null;
            }
        })();

        requestRef.current = request;
        return request;
    }, []);

    useEffect(() => {
        pullProfile(true);
        const intervalId = setInterval(() => {
            pullProfile();
        }, POLL_INTERVAL_MS);

        return () => {
            clearInterval(intervalId);
        };
    }, [pullProfile]);

    const refresh = useCallback(() => pullProfile(true), [pullProfile]);

    const value = useMemo<UserStatsContextValue>(() => ({
        profile,
        loading,
        error,
        lastUpdated,
        refresh,
    }), [profile, loading, error, lastUpdated, refresh]);

    return (
        <UserStatsContext.Provider value={value}>
            {children}
        </UserStatsContext.Provider>
    );
}

export function useUserStats() {
    const context = useContext(UserStatsContext);
    if (!context) {
        throw new Error("useUserStats must be used within a UserStatsProvider");
    }
    return context;
}

